import {
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  collection
} from 'firebase/firestore'
import { firestore } from '../firebase/firebaseClient'
import { extractDataSnapshot, mergeDataSnapshots } from './dataSnapshot'
import {
  CATEGORY_DOCUMENTS,
  FIRESTORE_COLLECTIONS,
  GENERAL_INFO_DOCUMENTS,
  LOCAL_COLLECTION_TO_FIRESTORE,
  firestoreCollectionForLocalKey,
  storageFolderForCollection
} from './firebaseModel'
import defaultDbTemplate from './defaultDbTemplate.json'

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function reverseMap(map) {
  return Object.fromEntries(Object.entries(map).map(([key, value]) => [value, key]))
}

function docData(snapshot) {
  return snapshot.docs.map(item => ({
    id: item.id,
    ...item.data()
  }))
}

async function loadDefaultData() {
  return extractDataSnapshot(defaultDbTemplate)
}

async function loadCollectionItems(collectionName) {
  const snapshot = await getDocs(collection(firestore, collectionName))
  return docData(snapshot)
}

async function loadOptionalCollectionItems(collectionName) {
  try {
    return await loadCollectionItems(collectionName)
  } catch (error) {
    if (error && error.code === 'permission-denied') return []
    throw error
  }
}

async function saveCollectionItemDoc(collectionName, item) {
  await setDoc(doc(firestore, collectionName, item.id), clone(item), { merge: false })
}

async function deleteCollectionItemDoc(collectionName, itemId) {
  await deleteDoc(doc(firestore, collectionName, itemId))
}

async function loadCategories() {
  const result = {}
  const snapshot = await getDocs(collection(firestore, FIRESTORE_COLLECTIONS.categories))
  const docs = Object.fromEntries(snapshot.docs.map(item => [item.id, item.data()]))

  Object.entries(CATEGORY_DOCUMENTS).forEach(([localKey, documentId]) => {
    if (docs[documentId]) result[localKey] = docs[documentId].items || []
  })

  return result
}

async function saveCategoryGroupDoc(localKey, items = []) {
  const documentId = CATEGORY_DOCUMENTS[localKey]
  if (!documentId) throw new Error(`Unknown category group: ${localKey}`)
  const safeItems = Array.isArray(items) ? items : []
  const snapshot = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.categories, documentId))
  const remoteItems = snapshot.exists() && Array.isArray(snapshot.data().items) ? snapshot.data().items : []
  const nextIds = new Set(safeItems.map(item => item.id))
  const preservedRemoteItems = remoteItems.filter(item => {
    return item && item.id && !nextIds.has(item.id)
  })
  const nextItems = clone([...safeItems, ...preservedRemoteItems])
  await setDoc(doc(firestore, FIRESTORE_COLLECTIONS.categories, documentId), {
    items: nextItems
  }, { merge: false })
  return nextItems
}

async function deleteCategoryItemDoc(localKey, itemId) {
  const documentId = CATEGORY_DOCUMENTS[localKey]
  if (!documentId) throw new Error(`Unknown category group: ${localKey}`)
  const snapshot = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.categories, documentId))
  const remoteItems = snapshot.exists() && Array.isArray(snapshot.data().items) ? snapshot.data().items : []
  const nextItems = remoteItems.filter(item => item && item.id !== itemId)
  await setDoc(doc(firestore, FIRESTORE_COLLECTIONS.categories, documentId), {
    items: clone(nextItems)
  }, { merge: false })
  return itemId
}

async function loadGeneralInfo() {
  const snapshot = await getDocs(collection(firestore, FIRESTORE_COLLECTIONS.generalInfo))
  const docs = Object.fromEntries(snapshot.docs.map(item => [item.id, item.data()]))

  return {
    general: docs[GENERAL_INFO_DOCUMENTS.general]
      ? { sections: docs[GENERAL_INFO_DOCUMENTS.general].sections || [] }
      : null,
    pages: docs[GENERAL_INFO_DOCUMENTS.pages]
      ? docs[GENERAL_INFO_DOCUMENTS.pages].pages || {}
      : null
  }
}

async function saveGeneralDocument(general) {
  const snapshot = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.generalInfo, GENERAL_INFO_DOCUMENTS.general))
  const existingGeneral = snapshot.exists() ? snapshot.data() : {}
  const nextGeneral = preserveRemoteNonEmptyArrays(existingGeneral, {
    sections: clone((general || {}).sections || [])
  })
  await setDoc(doc(firestore, FIRESTORE_COLLECTIONS.generalInfo, GENERAL_INFO_DOCUMENTS.general), nextGeneral, { merge: false })
  return nextGeneral
}

async function saveSinglePageDocument(pageKey, content) {
  const snapshot = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.generalInfo, GENERAL_INFO_DOCUMENTS.pages))
  const existingPages = snapshot.exists() && snapshot.data().pages ? snapshot.data().pages : {}
  const nextPages = {
    ...clone(existingPages),
    [pageKey]: clone(content)
  }
  await setDoc(doc(firestore, FIRESTORE_COLLECTIONS.generalInfo, GENERAL_INFO_DOCUMENTS.pages), {
    pages: nextPages
  }, { merge: false })
  return nextPages
}

function preserveRemoteNonEmptyArrays(remoteValue, nextValue) {
  if (Array.isArray(nextValue)) {
    return !nextValue.length && Array.isArray(remoteValue) && remoteValue.length ? clone(remoteValue) : nextValue
  }

  if (!nextValue || typeof nextValue !== 'object') return nextValue
  if (!remoteValue || typeof remoteValue !== 'object' || Array.isArray(remoteValue)) return nextValue

  const result = { ...nextValue }
  Object.keys(remoteValue).forEach(key => {
    if (result[key] === undefined) {
      result[key] = clone(remoteValue[key])
      return
    }
    result[key] = preserveRemoteNonEmptyArrays(remoteValue[key], result[key])
  })
  return result
}

export function createFirebaseDataRepository() {
  const firestoreToLocalCollection = reverseMap(LOCAL_COLLECTION_TO_FIRESTORE)

  return {
    async loadDb() {
      const defaultData = await loadDefaultData()
      const [categories, generalInfo, collectionEntries] = await Promise.all([
        loadCategories(),
        loadGeneralInfo(),
        Promise.all(Object.values(LOCAL_COLLECTION_TO_FIRESTORE).map(async firestoreCollection => {
          const localCollection = firestoreToLocalCollection[firestoreCollection]
          const loader = (
            firestoreCollection === FIRESTORE_COLLECTIONS.testBank ||
            firestoreCollection === FIRESTORE_COLLECTIONS.testAttempts
          ) ? loadOptionalCollectionItems : loadCollectionItems
          return [localCollection, { items: await loader(firestoreCollection) }]
        }))
      ])

      const remoteData = {
        version: defaultData.version,
        languages: defaultData.languages,
        categories,
        general: generalInfo.general,
        pages: generalInfo.pages,
        collections: Object.fromEntries(collectionEntries)
      }

      return mergeDataSnapshots(defaultData, remoteData)
    },
    async saveGeneral(general) {
      const nextGeneral = await saveGeneralDocument(general)
      return { sections: nextGeneral.sections || [] }
    },
    async savePage(pageKey, content) {
      return saveSinglePageDocument(pageKey, content)
    },
    async saveCategoryGroup(categoryKey, items) {
      return saveCategoryGroupDoc(categoryKey, items)
    },
    async deleteCategoryItem(categoryKey, itemId) {
      return deleteCategoryItemDoc(categoryKey, itemId)
    },
    async saveCollectionItem(localCollection, item) {
      const firestoreCollection = firestoreCollectionForLocalKey(localCollection)
      await saveCollectionItemDoc(firestoreCollection, item)
      return clone(item)
    },
    async deleteCollectionItem(localCollection, itemId) {
      const firestoreCollection = firestoreCollectionForLocalKey(localCollection)
      await deleteCollectionItemDoc(firestoreCollection, itemId)
      return itemId
    },
    async resetDb() {
      return this.loadDb()
    }
  }
}

export {
  CATEGORY_DOCUMENTS,
  FIRESTORE_COLLECTIONS,
  GENERAL_INFO_DOCUMENTS,
  firestoreCollectionForLocalKey,
  storageFolderForCollection
}
